import React, { useContext } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import { useNavigate } from "react-router";
import { useTextDomainContext } from "@/app/providers";

/**
 * Professional Error 500 / Internal Server Error page component.
 * Rendered when a server error occurs or an unexpected application failure occurs.
 */
export const Error500: React.FC = () => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const navigate = useNavigate();
  const theme = useTheme();

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Box
      sx={{
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, sm: 6 },
            textAlign: "center",
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.default, 0.95)})`
                : `linear-gradient(145deg, #ffffff, ${alpha(theme.palette.primary.light, 0.04)})`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 20px 40px rgba(0,0,0,0.5)"
                : "0 20px 40px rgba(0,0,0,0.06)",
            backdropFilter: "blur(10px)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Background Ambient Glow Accents */}
          <Box
            sx={{
              position: "absolute",
              top: -60,
              right: -60,
              width: 180,
              height: 180,
              borderRadius: "50%",
              background: alpha(theme.palette.error.main, 0.12),
              filter: "blur(40px)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -60,
              left: -60,
              width: 180,
              height: 180,
              borderRadius: "50%",
              background: alpha(theme.palette.primary.main, 0.08),
              filter: "blur(40px)",
              pointerEvents: "none",
            }}
          />

          <Stack
            spacing={3}
            sx={{ alignItems: "center", position: "relative", zIndex: 1 }}
          >
            {/* Status Chip */}
            <Chip
              icon={
                <ReportProblemOutlinedIcon
                  sx={{ fontSize: "16px !important" }}
                />
              }
              label="ERROR 500"
              color="error"
              size="small"
              sx={{
                fontWeight: 700,
                letterSpacing: 1.2,
                px: 1,
                py: 0.5,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.error.main, 0.12),
                color: theme.palette.error.main,
                border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
              }}
            />

            {/* Icon Container with glowing ring */}
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: 96,
                  height: 96,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: alpha(theme.palette.error.main, 0.08),
                  border: `2px solid ${alpha(theme.palette.error.main, 0.25)}`,
                  color: theme.palette.error.main,
                  boxShadow: `0 0 24px ${alpha(theme.palette.error.main, 0.15)}`,
                }}
              >
                <DnsOutlinedIcon sx={{ fontSize: 48 }} />
              </Box>
            </Box>

            {/* Title & Description */}
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 800,
                  mb: 1.5,
                  letterSpacing: "-0.02em",
                  color: theme.palette.text.primary,
                }}
              >
                {gettext("Something Went Wrong")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  maxWidth: 420,
                  mx: "auto",
                  lineHeight: 1.6,
                }}
              >
                {gettext(
                  "An unexpected error occurred while processing your request. Please try refreshing the page or return to the dashboard.",
                )}
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ pt: 1, width: "100%", justifyContent: "center" }}
            >
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{
                  borderRadius: 2.5,
                  px: 2.5,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: theme.palette.divider,
                  "&:hover": {
                    borderColor: theme.palette.text.primary,
                    backgroundColor: alpha(theme.palette.text.primary, 0.04),
                  },
                }}
              >
                {gettext("Reload Page")}
              </Button>

              <Button
                variant="outlined"
                color="inherit"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{
                  borderRadius: 2.5,
                  px: 2.5,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: theme.palette.divider,
                  "&:hover": {
                    borderColor: theme.palette.text.primary,
                    backgroundColor: alpha(theme.palette.text.primary, 0.04),
                  },
                }}
              >
                {gettext("Go Back")}
              </Button>

              <Button
                variant="contained"
                color="primary"
                startIcon={<HomeOutlinedIcon />}
                onClick={() => navigate("/")}
                sx={{
                  borderRadius: 2.5,
                  px: 2.5,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.24)}`,
                }}
              >
                {gettext("Dashboard")}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};
